export const toPx = function toPx(val: number, k: number) {
  return Math.floor(val * k / 72 * 96);
};

export const createElement = function createElement(tagName: string, opt: { className?: string; innerHTML?: string; style?: object }) {
  const el = document.createElement(tagName);
  if (opt.className) {
    el.className = opt.className;
  }
  if (opt.innerHTML) {
    el.innerHTML = opt.innerHTML;
    const scripts = el.getElementsByTagName('script');
    for (var i = scripts.length; i-- > 0; null) {
      scripts[i].parentNode.removeChild(scripts[i]);
    }
  }
  for (var key in opt.style) {
    el.style[key] = opt.style[key];
  }
  return el;
};

export function pageBreaks(root: HTMLElement, pxPageHeight: number) {
  const modeSrc = ['avoid-all', 'css'];
  const mode = {
    avoidAll: modeSrc.includes('avoid-all'),
    css: modeSrc.includes('css'),
  };
  const els = root.querySelectorAll('*');
  for (const el of Array.from(els)) {
    // Setup pagebreak rules based on legacy and avoidAll modes.
    let rules = {
      before: false,
      after: false,
      avoid: mode.avoidAll,
    };

    // Add rules for css mode.
    if (mode.css) {
      // TODO: Check if this is valid with iFrames.
      const style = window.getComputedStyle(el);
      // TODO: Handle 'left' and 'right' correctly.
      // TODO: Add support for 'avoid' on breakBefore/After.
      const breakOpt = ['always', 'page', 'left', 'right'];
      const avoidOpt = ['avoid', 'avoid-page'];
      rules = {
        before: rules.before || breakOpt.includes(style.breakBefore),
        after: rules.after || breakOpt.includes(style.breakAfter),
        avoid: rules.avoid || avoidOpt.includes(style.breakInside),
      };
    }

    // Get element position on the screen.
    // TODO: Subtract the top of the container from clientRect.top/bottom?
    const clientRect = el.getBoundingClientRect();
    // console.log(el.textContent, clientRect.top, clientRect.bottom, pxPageHeight);

    // Avoid: Check if a break happens mid-element.
    if (rules.avoid && !rules.before) {
      const startPage = Math.floor(clientRect.top / pxPageHeight);
      const endPage = Math.floor(clientRect.bottom / pxPageHeight);
      const nPages = Math.abs(clientRect.bottom - clientRect.top) / pxPageHeight;

      // Turn on rules.before if the el is broken and is at most one page long.
      if (endPage !== startPage && nPages <= 1) {
        rules.before = true;
      }
    }

    // Before: Create a padding div to push the element to the next page.
    if (rules.before) {
      const height = pxPageHeight - (clientRect.top % pxPageHeight) + 'px';
      const pad = createElement('div', {
        style: {
          display: 'block',
          height,
          // backgroundColor: '#222',
        },
      });
      el.parentNode.insertBefore(pad, el);
    }

    // After: Create a padding div to fill the remaining page.
    if (rules.after) {
      const height = pxPageHeight - (clientRect.bottom % pxPageHeight) + 'px';
      const pad = createElement('div', {
        style: {
          display: 'block',
          height,
        },
      });
      el.parentNode.insertBefore(pad, el.nextSibling);
    }
  }
}

export function setContainer(src: HTMLElement, width: number, unit: string) {
  const overlayCSS = {
    position: 'fixed', overflow: 'hidden', zIndex: 1000,
    left: 0, right: 0, bottom: 0, top: 0,
    backgroundColor: 'rgba(0,0,0,0.8)', opacity: 0,
  };
  const containerCSS = {
    position: 'absolute', width: width + unit,
    left: 0, right: 0, top: 0, height: 'auto', margin: 'auto',
    backgroundColor: 'white',
  };
  const overlay = createElement('div', { style: overlayCSS });
  const container = createElement('div', { style: containerCSS });
  container.appendChild(src);
  overlay.appendChild(container);
  document.body.appendChild(overlay);
  return [overlay, container];
}

export function getPageSize(orientation: string, unit: string, format: string) {
  // Default options
  unit = unit || 'mm';
  format = format || 'a4';
  orientation = ('' + (orientation || 'P')).toLowerCase();

  // Size in pt of various paper formats
  const pageFormats = {
    'a0': [2383.94, 3370.39], 'a1': [1683.78, 2383.94],
    'a2': [1190.55, 1683.78], 'a3': [841.89, 1190.55],
    'a4': [595.28, 841.89], 'a5': [419.53, 595.28],
    'a6': [297.64, 419.53], 'a7': [209.76, 297.64],
    'a8': [147.40, 209.76], 'a9': [104.88, 147.40],
    'a10': [73.70, 104.88], 'b0': [2834.65, 4008.19],
    'b1': [2004.09, 2834.65], 'b2': [1417.32, 2004.09],
    'b3': [1000.63, 1417.32], 'b4': [708.66, 1000.63],
    'b5': [498.90, 708.66], 'b6': [354.33, 498.90],
    'b7': [249.45, 354.33], 'b8': [175.75, 249.45],
    'b9': [124.72, 175.75], 'b10': [87.87, 124.72],
    'c0': [2599.37, 3676.54], 'c1': [1836.85, 2599.37],
    'c2': [1298.27, 1836.85], 'c3': [918.43, 1298.27],
    'c4': [649.13, 918.43], 'c5': [459.21, 649.13],
    'c6': [323.15, 459.21], 'c7': [229.61, 323.15],
    'c8': [161.57, 229.61], 'c9': [113.39, 161.57],
    'c10': [79.37, 113.39], 'dl': [311.81, 623.62],
    'letter': [612, 792],
    'government-letter': [576, 756],
    'legal': [612, 1008],
    'junior-legal': [576, 360],
    'ledger': [1224, 792],
    'tabloid': [792, 1224],
    'credit-card': [153, 243],
  };

  let k = 1;
  // Unit conversion
  switch (unit) {
    case 'pt': k = 1; break;
    case 'mm': k = 72 / 25.4; break;
    case 'cm': k = 72 / 2.54; break;
    case 'in': k = 72; break;
    case 'px': k = 72 / 96; break;
    case 'pc': k = 12; break;
    case 'em': k = 12; break;
    case 'ex': k = 6; break;
    default:
      throw new Error('Invalid unit: ' + unit);
  }

  let pageHeight = pageFormats[format][1] / k;
  let pageWidth = pageFormats[format][0] / k;

  // Handle page orientation
  if (orientation === 'p' || orientation === 'portrait') {
    orientation = 'p';
    if (pageWidth > pageHeight) {
      const tmp = pageWidth;
      pageWidth = pageHeight;
      pageHeight = tmp;
    }
  } else if (orientation === 'l' || orientation === 'landscape') {
    orientation = 'l';
    if (pageHeight > pageWidth) {
      const tmp = pageWidth;
      pageWidth = pageHeight;
      pageHeight = tmp;
    }
  } else {
    throw Error('Invalid orientation: ' + orientation);
  }

  // Return information (k is the unit conversion ratio from pts)
  return { width: pageWidth, height: pageHeight, unit, k };
}
